import Badge from '@src/components/atoms/badges/badge';
import TransactionInProgressBadge from '@src/components/atoms/badges/transactionInProgressBadge';
import Button from '@src/components/atoms/buttons/button';
import MetadataErrorCard from '@src/components/cards/metadataErrorCard';
import { useModal } from '@src/components/modal-views/context';
import type { MetadataValidationResponse } from '@src/models/dtos/metadata';
import type { IGovernanceAction } from '@src/models/types';
import { useAppDispatch, useAppSelector } from '@src/store/hooks';
import { useLazyGetTransactionStatusQuery } from '@src/store/transaction/api';
import { resetVotingPendingTransaction } from '@src/store/transaction/transaction';
import { isEmpty } from 'lodash';
import React, { useEffect } from 'react';

export default function TopContainer({
  governanceAction,
  metadataResponse,
  isdRepRegistered,
  isGetDRepDetailsLoading,
  votedType,
  renderVotedCard,
  dRepId,
  hasServerHeader = false,
}: {
  governanceAction: IGovernanceAction;
  metadataResponse: MetadataValidationResponse | null;
  isdRepRegistered: boolean;
  isGetDRepDetailsLoading: boolean;
  votedType: string;
  renderVotedCard: Function;
  dRepId: string;
  hasServerHeader?: boolean;
}) {
  const { expireAt, createdAt } = governanceAction;
  const title =
    metadataResponse?.metadata?.title || governanceAction.meta?.title;

  const isExpiredProposal = new Date(expireAt.time) < new Date();
  const { openModal } = useModal();
  const proposalId =
    createdAt.index != null
      ? `${createdAt.tx}#${createdAt.index}`
      : createdAt.tx;
  const wallet = useAppSelector((state) => state.wallet);
  const pendingTransactions = useAppSelector(
    (state) => state.pendingTransactions
  );

  const dispatch = useAppDispatch();
  const [fetchTransactionStatus] = useLazyGetTransactionStatusQuery();

  useEffect(() => {
    const checkTransactionStatus = async () => {
      const { pendingVotingTransaction } = pendingTransactions;

      if (pendingVotingTransaction.txId) {
        const isExpired =
          pendingVotingTransaction.time + 4 * 60 * 1000 < Date.now();

        if (isExpired) {
          dispatch(resetVotingPendingTransaction());
        }

        try {
          const response = await fetchTransactionStatus(
            pendingVotingTransaction.txId
          );
          if (response.data?.isTransactionConfirmed) {
            dispatch(resetVotingPendingTransaction());
            window.location.reload();
          }
        } catch (error) {
          console.error('Failed to fetch transaction status', error);
        }
      }
    };

    const intervalId = setInterval(checkTransactionStatus, 5000);

    return () => {
      clearInterval(intervalId);
    };
  }, [dispatch, fetchTransactionStatus, pendingTransactions]);

  const renderVoteButton = () => {
    if (isExpiredProposal || wallet.instance === null) return null;
    if (
      pendingTransactions.pendingVotingTransaction.name ===
      proposalId + dRepId
    )
      return <TransactionInProgressBadge />;
    if (!isdRepRegistered && isGetDRepDetailsLoading) {
      return (
        <div className="r h-9 animate-pulse rounded-xl bg-neutral-700 text-transparent">
          Cast your vote
        </div>
      );
    }
    if (isdRepRegistered && !isGetDRepDetailsLoading && isEmpty(votedType)) {
      return (
        <Button
          onClick={() => openModal('CAST_VOTE', { proposalId })}
          buttonSize="xsmall"
          variant="primary"
        >
          Cast your vote
        </Button>
      );
    }
  };

  return (
    <React.Fragment>
      <MetadataErrorCard metadataResponse={metadataResponse} />
      {renderVotedCard('md:hidden')}
      {/*
        The title, status, dates and action type live in the page header
        (`components/seo/proposalSummary`) — they used to be repeated here,
        directly below it. That header is only rendered for a proposal the
        indexer knows, so when it is absent the title falls back to here rather
        than the page having none at all.
      */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        {hasServerHeader ? (
          <span />
        ) : (
          <div className="flex items-center gap-2">
            {/*
              `h1`, and from the indexed copy when the anchor document has not
              validated: the page header only renders for a proposal the SEO
              catalog knows, so without this a proposal it does not know had no
              heading at all until an IPFS round trip finished.
            */}
            {title && (
              <h1 className="body18 font-bold dark:text-white">{title}</h1>
            )}
            <Badge
              className="w-fit"
              label={isExpiredProposal ? 'Expired' : 'Live'}
              color={isExpiredProposal ? 'error' : 'success'}
            />
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {renderVoteButton()}
          <Button
            onClick={() => openModal('SHARE')}
            buttonSize="xsmall"
            variant="gray"
          >
            Share
          </Button>
        </div>
      </div>
    </React.Fragment>
  );
}
